export const PR_STATUS = {
  NEW: "new",
  PENDING: "pending",
  INFO_REQUESTED: "info_requested",
  APPROVED: "approved",
  REJECTED: "rejected",
}

export const PR_STATUS_LABEL = {
  [PR_STATUS.NEW]: "New",
  [PR_STATUS.PENDING]: "Waiting for approval",
  [PR_STATUS.INFO_REQUESTED]: "Info requested",
  [PR_STATUS.APPROVED]: "Approved",
  [PR_STATUS.REJECTED]: "Rejected",
}

export const PR_STATUS_COLOR = {
  [PR_STATUS.NEW]: "secondary",
  [PR_STATUS.PENDING]: "info",
  [PR_STATUS.INFO_REQUESTED]: "warning",
  [PR_STATUS.APPROVED]: "success",
  [PR_STATUS.REJECTED]: "error",
}

export const SUN_PAYMENT_STATUS = {
  UNPAID: "unpaid",
  PAID: "paid",
}

export const SUN_PAYMENT_LABEL = {
  [SUN_PAYMENT_STATUS.UNPAID]: "Unpaid",
  [SUN_PAYMENT_STATUS.PAID]: "Paid",
}

// ── Procurement V2 statuses (additive) ──────────────────────────────────────
// Order Request lifecycle. Mirrors the PR flow but adds an explicit Closed
// terminal state used by the requester workspace and My Orders tracking.
export const ORDER_STATUS = {
  // `draft` is the real initial state emitted by the backend (BA spec §
  // order_request enum: draft|pending|info_requested|approved|rejected|closed).
  // The mock V2 surfaces use `new` for the same stage; both must render.
  DRAFT: "draft",
  NEW: "new",
  PENDING: "pending",
  INFO_REQUESTED: "info_requested",
  APPROVED: "approved",
  REJECTED: "rejected",
  CLOSED: "closed",
}

export const ORDER_STATUS_LABEL = {
  [ORDER_STATUS.DRAFT]: "Draft",
  [ORDER_STATUS.NEW]: "New",
  [ORDER_STATUS.PENDING]: "Waiting for approval",
  [ORDER_STATUS.INFO_REQUESTED]: "Info requested",
  [ORDER_STATUS.APPROVED]: "Approved",
  [ORDER_STATUS.REJECTED]: "Rejected",
  [ORDER_STATUS.CLOSED]: "Closed",
}

// ── Purchase (PO) lifecycle ──────────────────────────────────────────────────
// create(draft) → submit → pending → approve → approved → issue → issued
//                   ├ info_request → info_requested → resubmit → pending
//                   └ reject → rejected
//                 (whenever valid) → cancel → cancelled
// issued → receive(complete=false) → receiving → receive(complete=true) → completed
//   (receiving/completed are goods-receipt states — see PO_ACTION_META.receive)
export const PO_STATUS = {
  DRAFT: "draft",
  PENDING: "pending",
  INFO_REQUESTED: "info_requested",
  APPROVED: "approved",
  ISSUED: "issued",
  RECEIVING: "receiving",
  COMPLETED: "completed",
  REJECTED: "rejected",
  CANCELLED: "cancelled",
}

export const PO_STATUS_LABEL = {
  [PO_STATUS.DRAFT]: "Draft",
  [PO_STATUS.PENDING]: "Waiting for approval",
  [PO_STATUS.INFO_REQUESTED]: "Info requested",
  [PO_STATUS.APPROVED]: "Approved",
  [PO_STATUS.ISSUED]: "Issued",
  [PO_STATUS.RECEIVING]: "Receiving",
  [PO_STATUS.COMPLETED]: "Completed",
  [PO_STATUS.REJECTED]: "Rejected",
  [PO_STATUS.CANCELLED]: "Cancelled",
}

// ── Payment Request (PR) lifecycle — last leg of P2P: OR → PO → GRN → PR → SUN.
// State machine is fixed by the BA spec — do not rename or add states here:
//   Draft → Pending → (InfoRequested ⇄ Pending) → Approved → SentToAccounting → Paid
//   Pending → Rejected
//   Draft → SentToAccounting (whenever the resolved approval rule requires no approval)
// "Paid" is set by a passive BE cron that scans SUN — there is no FE action for it.
export const PAYMENT_REQUEST_STATUS = {
  DRAFT: "draft",
  PENDING: "pending",
  INFO_REQUESTED: "info_requested",
  APPROVED: "approved",
  REJECTED: "rejected",
  SENT_TO_ACCOUNTING: "sent_to_accounting",
  PAID: "paid",
}

export const PAYMENT_REQUEST_STATUS_LABEL = {
  [PAYMENT_REQUEST_STATUS.DRAFT]: "Draft",
  [PAYMENT_REQUEST_STATUS.PENDING]: "Waiting for approval",
  [PAYMENT_REQUEST_STATUS.INFO_REQUESTED]: "Info requested",
  [PAYMENT_REQUEST_STATUS.APPROVED]: "Approved",
  [PAYMENT_REQUEST_STATUS.REJECTED]: "Rejected",
  [PAYMENT_REQUEST_STATUS.SENT_TO_ACCOUNTING]: "Sent to Accounting",
  [PAYMENT_REQUEST_STATUS.PAID]: "Paid",
}

export const KYC_STATUS = {
  PENDING: "kyc_pending",
  VERIFIED: "kyc_verified",
  REJECTED: "kyc_rejected",
}

export const MATCH_STATUS = {
  MATCH: "match",
  MISMATCH: "mismatch",
}

// Single generic badge map: status code → { label, color }. `color` is a
// theme palette key already used by StatusBadge/MDBox (info/success/etc.),
// so any new badge is visually indistinguishable from existing ones.
export const BADGE_MAP = {
  // PR / Order lifecycle
  draft: { label: "Draft", color: "secondary" },
  new: { label: "New", color: "secondary" },
  pending: { label: "Waiting for approval", color: "info" },
  info_requested: { label: "Info requested", color: "warning" },
  approved: { label: "Approved", color: "success" },
  rejected: { label: "Rejected", color: "error" },
  closed: { label: "Closed", color: "dark" },
  // Purchase (PO) lifecycle
  issued: { label: "Issued", color: "success" },
  receiving: { label: "Receiving", color: "warning" },
  completed: { label: "Completed", color: "success" },
  cancelled: { label: "Cancelled", color: "dark" },
  // Payment Request (PR) lifecycle — draft/pending/info_requested/approved/
  // rejected already share the generic entries above.
  sent_to_accounting: { label: "Sent to Accounting", color: "info" },
  // Payment
  unpaid: { label: "Unpaid", color: "warning" },
  paid: { label: "Paid", color: "success" },
  // 3-way match
  match: { label: "Match", color: "success" },
  mismatch: { label: "Mismatch", color: "error" },
  // Supplier KYC
  kyc_pending: { label: "KYC Pending", color: "warning" },
  kyc_verified: { label: "KYC Verified", color: "success" },
  kyc_rejected: { label: "KYC Rejected", color: "error" },
}

export const badgeFor = (status) =>
  BADGE_MAP[status] || { label: status || "—", color: "secondary" }

// Edit is for a not-yet-submitted draft, or a draft sent back for more info.
// `new` is the same initial stage on older/mock payloads. Info-requested opens
// this same form (PUT-backed) but its bottom action is Resubmit, not Submit —
// see OrderRequestFormContainer's `isInfoRequested` branch.
export const canEditOrder = (status) =>
  status === ORDER_STATUS.DRAFT || status === ORDER_STATUS.NEW || status === ORDER_STATUS.INFO_REQUESTED

// Edit follows the BE's allowedActions (AllowedActionsPolicy): "update" = owner
// on draft or procurement staff on pending; "resubmit" = owner answering an
// info_request, which uses the same form. canEditOrder stays the requester-flow
// subset (Submit / Resubmit); a pending edit saves via PUT only.
export const canOpenOrderEdit = (order) => {
  const actions = order?.allowedActions || []
  return actions.includes("update") || actions.includes("resubmit")
}

// Draft stays status-based: the BE doesn't list "update" for a draft PO, but
// PUT still lets its owner edit it.
export const canEditPurchase = (po) => {
  const actions = po?.allowedActions || []
  return po?.status === PO_STATUS.DRAFT || actions.includes("update") || actions.includes("resubmit")
}

// Resubmit is only valid while the order is waiting on requester info.
export const canResubmitOrder = (status) => status === ORDER_STATUS.INFO_REQUESTED

// Manager / approver action availability. Approve / Reject / Info-Request are
// exposed ONLY where the current user is the assigned approver (caller passes
// `isApprover`) and the document is awaiting a decision.
export const approverActions = (status, isApprover = false) => {
  const awaiting =
    status === PR_STATUS.PENDING || status === ORDER_STATUS.PENDING
  return {
    canApprove: !!isApprover && awaiting,
    canReject: !!isApprover && awaiting,
    canInfoRequest: !!isApprover && awaiting,
  }
}

// Requester action availability per status, per brief §7.3.
// Approve / Reject / Info-Request are NEVER on this list — Approver/Accountant only.
export const requesterActions = (status) => {
  const canResubmit = canResubmitOrder(status)
  const canEdit = canEditOrder(status)
  switch (status) {
    case ORDER_STATUS.DRAFT:
    case PR_STATUS.NEW:
      return {
        canEdit,
        canDelete: true,
        canSubmit: true,
        canResubmit,
        canPaymentReady: false,
        canUploadDocs: true,
        canExport: true,
      }
    case PR_STATUS.PENDING:
      return {
        canEdit,
        canDelete: false,
        canSubmit: false,
        canResubmit,
        canPaymentReady: false,
        canUploadDocs: false,
        canExport: true,
      }
    case PR_STATUS.INFO_REQUESTED:
      return {
        canEdit,
        canDelete: false,
        canSubmit: false,
        canResubmit,
        canPaymentReady: true, // post-approval accountant loop
        canUploadDocs: true,
        canExport: true,
      }
    case PR_STATUS.APPROVED:
      return {
        canEdit,
        canDelete: false,
        canSubmit: false,
        canResubmit,
        canPaymentReady: false,
        canUploadDocs: false,
        canExport: true,
      }
    case PR_STATUS.REJECTED:
      return {
        canEdit,
        canDelete: false,
        canSubmit: false,
        canResubmit,
        canPaymentReady: false,
        canUploadDocs: false,
        canExport: true,
      }
    default:
      return {
        canEdit,
        canDelete: false,
        canSubmit: false,
        canResubmit,
        canPaymentReady: false,
        canUploadDocs: false,
        canExport: false,
      }
  }
}
